import React from "react";
import { IndexRedirect, Route } from "react-router";

import Dashboard from "../dashboard/Dashboard";
import AdminPage from "../dashboard/admin/AdminPage";
import BlocsPage from "../dashboard/admin/blocs/BlocsPage";
import BlocPage from "../dashboard/admin/blocs/bloc/BlocPage";
import { BlocClientsPage } from "../dashboard/admin/blocs/bloc/clients/.";
import { BlocSettingsPage } from "../dashboard/admin/blocs/bloc/settings/.";
import GuaranteesPage from "../dashboard/admin/guarantees/GuaranteesPage";
import InsurersPage from "../dashboard/admin/insurers/InsurersPage";
import TaxesPage from "../dashboard/admin/taxes/TaxesPage";
import UsersPage from "../dashboard/admin/users/UsersPage";
import GroupsPage from "../dashboard/admin/groups/GroupsPage";
import AuditPage from "../dashboard/audit/AuditPage";
import ClientsPage from "../dashboard/clients/ClientsPage";
import ClientPage from "../dashboard/clients/client/ClientPage";
import BillingPeriodsPage from "../dashboard/clients/client/billingPeriods/BillingPeriodsPage";
import BulkSalariesPage from "../dashboard/clients/client/bulkSalaries/BulkSalariesPage";
import ClassesPage from "../dashboard/clients/client/classes/ClassesPage";
import ClientCoveragesPage from "../dashboard/clients/client/client-coverages/ClientCoveragesPage";
import ClientGuaranteesPage from "../dashboard/clients/client/client-guarantees/ClientGuaranteesPage";
import ClientAttachmentsPage from "../dashboard/clients/client/clientAttachments/ClientAttachmentsPage";
import ConfigurationPage from "../dashboard/clients/client/configuration/ConfigurationPage";
import DivisionsPage from "../dashboard/clients/client/divisions/DivisionsPage";
import InvoicesPage from "../dashboard/clients/client/invoices/InvoicesPage";
import MemberIdentifierConfigurationPage from "../dashboard/clients/client/member-identifier-configuration/MemberIdentifierConfigurationPage";
import { MembersPage } from "../dashboard/clients/client/members";
import {
  MemberPage,
  MemberProfile,
} from "../dashboard/clients/client/members/member";
import { MemberFilesPage } from "../dashboard/clients/client/member-files";
import BeneficiariesPage from "../dashboard/clients/client/members/member/beneficiaries/BeneficiariesPage";
import EmploymentsPage from "../dashboard/clients/client/members/member/employments/EmploymentsPage";
import MemberCoveragePage from "../dashboard/clients/client/members/member/coverage/MemberCoveragePage";
import DependentsPage from "../dashboard/clients/client/members/member/dependents/DependentsPage";
import DependentCoverages from "../dashboard/clients/client/members/member/dependents/coverages/DependentCoveragesDataTable";
import PremiumSimulatorPage from "../dashboard/clients/client/premiumSimulator/PremiumSimulatorPage";
import ClientReportsPage from "../dashboard/clients/client/reports/ClientReportsPage";
import { MANUAL, RECURRING } from "../dashboard/clients/enums/billingPeriodTypes";
import FollowUpsPage from "../dashboard/follow-ups/FollowUpsPage";
import FollowUpsListPage from "../dashboard/follow-ups/list/FollowUpsListPage";
import MessagesPage from "../dashboard/follow-ups/messages/MessagesPage";
import ImportExportPage from "../dashboard/importExport/ImportExportPage";
import ExportsPage from "../dashboard/importExport/exports/ExportsPage";
import AccountingExportsPage from "../dashboard/importExport/exports/accounting/AccountingExportsPage";
import ImportMembersPage from "../dashboard/importExport/importMembers/ImportMembersPage";
import SuppliersReportsPage from "../dashboard/importExport/suppliersReports/SuppliersReportsPage";
import SalesTaxesPage from "../dashboard/sales-taxes/SalesTaxesPage";
import SynchronizationDataTable from "../dashboard/synchronization/SynchronizationDataTable";
import LoginPage from "../login/LoginPage";
import MemberIdentifierRecoveryPage from "../member-identifier-recovery/MemberIdentifierRecoveryPage";
import PersonalDataPage from "../personal-data/PersonalDataPage";
import WithdrawalPage from "../withdrawal/WithdrawalPage";
import App from "./App";
import NotFoundPage from "./NotFoundPage";
import UnauthorizedPage from "./UnauthorizedPage";


import {
  ADMINISTRATOR,
  AGENT,
  HR_CLERK,
  DIRECTOR,
  FINANCE,
  FINTECH,
  INFORMATION_AGENT,
  MANAGER,
  PLAN_ADMINISTRATOR,
  INTERNAL_ROLES,
  HIGH_MANAGEMENT,
} from "../../utils/Roles";
import userHasAccess from "../../utils/userHasAccess";

import { checkClientBillingPeriodType, getClient, getClientStatusType } from "../common/RouteValidators/ClientValidationHelper";
import { getSessionCookies } from "../common/sessions/tokenHelper";
import simpleFetch from "../../../utils/simpleFetch";
import { IS_MINT_ENABLED } from "../../utils/data/models";
import { getDashboardIndex, getClientAccessRedirect } from "./routeHelpers";
import { AccountsPage } from "../dashboard/admin/accounts";
import BrokersPage from "../dashboard/admin/brokers/BrokersPage";
import ClientUsersPage from "../dashboard/clients/client/clientUsers/ClientUsersPage";
import SynchronizationsPage from "../dashboard/clients/client/synchronizations/SynchronizationsPage";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../dashboard/clients/enums/clientStatusTypes";
import ExamplePage from "../example/ExamplePage";
import ForgotPasswordPage from "../forgot-password/ForgotPasswordPage";
import loginApi from "../login/loginApi";
import ResetPasswordPage from "../reset-password/ResetPasswordPage";
import NoDataPage from "./NoDataPage";
import { TED_DESJARDINS } from "../dashboard/clients/enums/synchronizationOptions";
import { withV2Redirect, MIGRATED_PAGES } from "../../utils/v2Access";

export const getRoutes = () => {
  const userIsLoggedIn = (nextState, replace, callback) => {
    const [accessToken,] = getSessionCookies();
    if (!accessToken) {
      replace({
        pathname: "/login",
        state: { nextPathname: nextState.location.pathname },
      });
    }
    callback();
  };

  const userHasRole = async ({ atLeast, exactly, reverseCheck = false }, nextState, replace, callback) => {
    if (!localStorage.getItem("roleId")) {
      // User is logged in but has no role in local storage
      // Calling login to set role
      await loginApi.login();
    }

    let allowed = userHasAccess(atLeast) || userHasAccess(exactly, true);

    if (reverseCheck) allowed = !allowed;

    if (!allowed) {
      replace({
        pathname: "/unauthorized",
        state: { nextPathname: nextState.location.pathname },
      });
    }

    callback();
  };

  const dashboardIndex = () => {
    return getDashboardIndex();
  };

  const validateClientAccess = (nextState, replace, callback) => {
    const redirectPath = getClientAccessRedirect(
      nextState.params.clientId,
      nextState.location.pathname
    );

    if (redirectPath) {
      replace({
        pathname: redirectPath,
        state: { nextPathname: nextState.location.pathname },
      });
    }

    callback();
  };

  const validateBillingPeriodTypeClientAccess = async ({ billingPeriodType, atLeast, exactly }, nextState, replace, callback) => {
    if (atLeast || exactly) {
      /**
       * If we pass a role to the function so we can limit by role.
       */
      const allowed = userHasAccess(atLeast) || userHasAccess(exactly, true);
      if (!allowed) {
        replace({
          pathname: "/unauthorized",
          state: { nextPathname: nextState.location.pathname },
        });
      }

    }
    const isClientBPAuthorized = await checkClientBillingPeriodType(nextState.params.clientId, billingPeriodType);
    if (!isClientBPAuthorized) {
      replace({
        pathname: "/unauthorized",
        state: { nextPathname: nextState.location.pathname },
      });
    }
    callback();
  };

  const validateBulkSalaryAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);
    const isRecurring = await checkClientBillingPeriodType(nextState.params.clientId, RECURRING.value);
    const isPlanAdmin = userHasAccess([PLAN_ADMINISTRATOR], true);
    switch (clientStatusType) {

      case IMPLEMENTATION.value:
        /**
        * Exactly role limitation
        */
        if (userHasAccess([PLAN_ADMINISTRATOR], true)) {
          break;
        }
        if (!userHasAccess(MANAGER)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;

      case ACTIVE.value:


        if (!userHasAccess([DIRECTOR, MANAGER, ADMINISTRATOR, FINTECH], true) &&
          !(isPlanAdmin && isRecurring)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;

      case INACTIVE.value:
      case TERMINATED.value:

        if (!userHasAccess([DIRECTOR, MANAGER, ADMINISTRATOR, FINTECH], true)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
    }
    callback();
  };

  const validateReportsAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);
    const isRecurring = await checkClientBillingPeriodType(nextState.params.clientId, RECURRING.value);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
      case ACTIVE.value:
        if (!userHasAccess(MANAGER) &&
          !(userHasAccess([PLAN_ADMINISTRATOR], true) && isRecurring)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      default:
        if (!userHasAccess(MANAGER)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
    }
    callback();
  };

  const validateClassAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
        if (!userHasAccess(PLAN_ADMINISTRATOR)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      case ACTIVE.value:
      case INACTIVE.value:
      case TERMINATED.value:
        if (!userHasAccess(AGENT)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
    }
    callback();
  };

  const validatePremiumSimulatorAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);
    const isRecurring = await checkClientBillingPeriodType(nextState.params.clientId, RECURRING.value);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
      case ACTIVE.value:
        if (!userHasAccess(AGENT) &&
          !(userHasAccess([PLAN_ADMINISTRATOR], true) && isRecurring)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      default:
        if (!userHasAccess(AGENT)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
    }
    callback();
  };

  const validateClientCoveragesAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);
    switch (clientStatusType) {
      case IMPLEMENTATION.value:
        if (!userHasAccess(FINANCE)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      case ACTIVE.value:
      case INACTIVE.value:
      case TERMINATED.value:
        if (!userHasAccess(AGENT)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
    }
    callback();
  };

  const validateClientUsersForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
      case ACTIVE.value:
      case INACTIVE.value:
      case TERMINATED.value:
        if (!userHasAccess(MANAGER)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
    }

    callback();
  };

  const validateMemberCoveragesAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
        if (!userHasAccess(PLAN_ADMINISTRATOR)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      case ACTIVE.value:
        break; /** All roles can access member coverages page for active clients. */
      case INACTIVE.value:
      case TERMINATED.value:
        if (!userHasAccess(AGENT)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
    }

    callback();
  };

  const validateInvoicesAccessForClientStatusType = async (nextState, replace, callback) => {
    const clientStatusType = await getClientStatusType(nextState.params.clientId);

    switch (clientStatusType) {
      case IMPLEMENTATION.value:
        if (!userHasAccess(PLAN_ADMINISTRATOR)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
        break;
      case ACTIVE.value:
      case INACTIVE.value:
      case TERMINATED.value:
        if (!userHasAccess([DIRECTOR, FINANCE, MANAGER, PLAN_ADMINISTRATOR, INFORMATION_AGENT, ADMINISTRATOR, FINTECH], true)) {
          redirectToUnauthorized(nextState.location.pathname, replace);
        }
    }

    callback();
  };

  const validateMemberFilesAccess = async (nextState, replace, callback) => {
    /**
     * Validate member files access by checking:
     * 1. User has INTERNAL_ROLES
     * 2. Client billing period is RECURRING
     * 3. MINT is enabled
     */
    const allowed = userHasAccess(INTERNAL_ROLES, true);
    if (!allowed) {
      redirectToUnauthorized(nextState.location.pathname, replace);
      callback();
      return;
    }

    try {
      // Check if client billing period is RECURRING or is of TED_DESJARDINS synctype
      const client = await getClient(nextState.params.clientId);
      if (client.clientBillingPeriodType != RECURRING.value || client.syncType == TED_DESJARDINS.value) {
        redirectToUnauthorized(nextState.location.pathname, replace);
        callback();
        return;
      }

      // Check if MINT is enabled
      const mintData = await simpleFetch("GET", IS_MINT_ENABLED.path, {});
      if (!mintData || mintData.isEnabled !== true) {
        redirectToUnauthorized(nextState.location.pathname, replace);
        callback();
        return;
      }

      callback();
    } catch (error) {
      // On authorization error or any other error, deny access
      redirectToUnauthorized(nextState.location.pathname, replace);
      callback();
    }
  };

  const redirectToUnauthorized = (pathname, replace) => {
    replace({
      pathname: "/unauthorized",
      state: { nextPathname: pathname },
    });
  };

  const setLanguageCodeAsParam = (
    languageCode,
    nextState,
    replace,
    callback
  ) => {
    nextState.params.languageCode = languageCode;
    callback();
  };

  return (
    <Route path="/" component={App} name="Gestion Major">
      <IndexRedirect to="login" />
      <Route path="login" component={LoginPage} name="Login" />
      <Route
        path="forgot-password"
        component={ForgotPasswordPage}
        name="ForgotPassword"
      />
      <Route path="dashboard" component={Dashboard} onEnter={userIsLoggedIn}>
        <IndexRedirect to={dashboardIndex()} />
        <Route
          path="no-data"
          component={NoDataPage}
          name="No data" />
        <Route
          path="synchronizations"
          component={SynchronizationDataTable}
          name="Synchronizations"
          onEnter={userHasRole.bind(null, {
            exactly: [DIRECTOR, FINANCE, MANAGER, ADMINISTRATOR, FINTECH, AGENT],
          })}
        />
        <Route
          path="admin"
          component={AdminPage}
          name="Admin"
          onEnter={userHasRole.bind(null, { atLeast: MANAGER })}
        >
          <IndexRedirect to="insurers" />
          <Route
            path="guarantees"
            component={GuaranteesPage}
            name="guarantees"
          />
          <Route path="brokers" component={BrokersPage} name="Brokers" />
          <Route path="insurers" component={InsurersPage} name="Insurers" />
          <Route
            path="users"
            component={UsersPage}
            name="Users"
            onEnter={userHasRole.bind(null, { atLeast: DIRECTOR })}
          />
          <Route
            path="groups"
            component={GroupsPage}
            name="Groups"
            onEnter={userHasRole.bind(null, { atLeast: DIRECTOR })}
          />
          <Route path="blocs" component={BlocsPage} name="Blocs">
            <Route path=":blocId" component={BlocPage} name="Bloc">
              <IndexRedirect to="clients" />
              <Route
                path="clients"
                component={BlocClientsPage}
                name="Clients"
                onEnter={validateClientAccess}
              />
              <Route
                path="settings"
                component={BlocSettingsPage}
                name="Settings"
              />
            </Route>
          </Route>
          <Route
            path="accounts"
            component={AccountsPage}
            name="Accounts"
            onEnter={userHasRole.bind(null, {
              exactly: [FINANCE, ADMINISTRATOR, FINTECH],
            })}
          />
          <Route
            path="taxes"
            component={TaxesPage}
            name="Taxes"
            onEnter={userHasRole.bind(null, { exactly: [FINANCE, ADMINISTRATOR, FINTECH] })}
          />
        </Route>
        <Route
          path="audit"
          component={AuditPage}
          name="Audit"
          onEnter={userHasRole.bind(null, { atLeast: DIRECTOR })}
        />
        <Route
          path="clients"
          component={ClientsPage}
          name="Clients"
          onEnter={validateClientAccess}
        >
          <Route
            path=":clientId"
            component={ClientPage}
            name="Client"
            onEnter={validateClientAccess}
          >
            <IndexRedirect to="members" />
            <Route path="members" component={MembersPage} name="Members" />
            <Route path="members/:memberId" component={MemberPage} name="Admin">
              <IndexRedirect to="profile" name="Admin" />
              <Route path="profile" component={MemberProfile} name="Members" onEnter={withV2Redirect(MIGRATED_PAGES['/dashboard/clients/:clientId/members/:memberId/profile'])} />
              <Route
                path="coverage"
                component={MemberCoveragePage}
                name="Coverage"
                onEnter={withV2Redirect(
                  MIGRATED_PAGES['/dashboard/clients/:clientId/members/:memberId/coverage'],
                  validateMemberCoveragesAccessForClientStatusType.bind(null)
                )}
              />
              <Route
                path="beneficiaries"
                component={BeneficiariesPage}
                name="Beneficiaries"
                onEnter={withV2Redirect(MIGRATED_PAGES['/dashboard/clients/:clientId/members/:memberId/beneficiaries'])}
              />
              <Route
                path="employments"
                component={EmploymentsPage}
                name="employments"
                onEnter={withV2Redirect(
                  MIGRATED_PAGES['/dashboard/clients/:clientId/members/:memberId/employments'],
                  validateBillingPeriodTypeClientAccess.bind(null, {
                    billingPeriodType: RECURRING.value,
                  })
                )}
              />
              <Route
                path="dependents"
                component={DependentsPage}
                name="Dependents"
                onEnter={withV2Redirect(MIGRATED_PAGES['/dashboard/clients/:clientId/members/:memberId/dependents'])}
              >
                <Route
                  path=":dependentId"
                  component={DependentCoverages}
                  name="DependentCoverages"
                />
              </Route>
            </Route>
            <Route
              path="classes"
              component={ClassesPage}
              name="Classes"
              onEnter={validateClassAccessForClientStatusType.bind(null)}
            />
            <Route
              path="divisions"
              component={DivisionsPage}
              name="Divisions"
              onEnter={userHasRole.bind(null, {
                atLeast: AGENT,
                exactly: [INFORMATION_AGENT, PLAN_ADMINISTRATOR],
              })}
            />
            <Route
              path="billingPeriods"
              component={BillingPeriodsPage}
              name="BillingPeriods"
              onEnter={userHasRole.bind(null, {
                exactly: HR_CLERK,
                reverseCheck: true
              })
              }
            />
            <Route
              path="client-coverages"
              component={ClientCoveragesPage}
              name="ClientCoverages"
              onEnter={validateClientCoveragesAccessForClientStatusType.bind(null)}
            />
            <Route
              path="client-guarantees"
              component={ClientGuaranteesPage}
              name="ClientGuarantees"
              onEnter={userHasRole.bind(null, {
                exactly: HIGH_MANAGEMENT,
              })}
            />
            <Route
              path="invoices"
              component={InvoicesPage}
              name="Invoices"
              onEnter={validateInvoicesAccessForClientStatusType.bind(null)}
            />
            <Route
              path="configurations"
              component={ConfigurationPage}
              name="Configurations"
              onEnter={userHasRole.bind(null, { atLeast: MANAGER })}
            />
            <Route
              path="member-identifier"
              component={MemberIdentifierConfigurationPage}
              name="MemberIdentifierConfiguration"
              onEnter={userHasRole.bind(null, { atLeast: AGENT })}
            />
            <Route
              path="reports"
              component={ClientReportsPage}
              name="Reports"
              onEnter={validateReportsAccessForClientStatusType.bind(null)}
            />
            <Route
              path="client-attachments"
              component={ClientAttachmentsPage}
              name="ClientAttachments"
              onEnter={userHasRole.bind(null, {
                exactly: HR_CLERK,
                reverseCheck: true
              })

              }
            />
            <Route
              path="bulk-salaries"
              component={BulkSalariesPage}
              name="BulkSalaries"
              onEnter={validateBulkSalaryAccessForClientStatusType.bind(null)}
            />
            <Route
              path="premium-simulator"
              component={PremiumSimulatorPage}
              name="PremiumSimulator"
              onEnter={validatePremiumSimulatorAccessForClientStatusType.bind(null)}
            />
            <Route
              path="users"
              component={ClientUsersPage}
              name="ClientUsers"
              onEnter={validateClientUsersForClientStatusType.bind(null)}
            />
            <Route
              path="synchronizations"
              component={SynchronizationsPage}
              name="Synchronizations"
              onEnter={validateBillingPeriodTypeClientAccess.bind(null, {
                billingPeriodType: MANUAL.value,
                exactly: [ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, FINANCE],
              })}
            />
            <Route
              path="member-files"
              component={MemberFilesPage}
              name="MemberFiles"
              onEnter={validateMemberFilesAccess.bind(null)}
            />
          </Route>
        </Route>
        <Route
          path="follow-ups"
          component={FollowUpsPage}
          name="FollowUps"
          onEnter={withV2Redirect(
            MIGRATED_PAGES['/dashboard/follow-ups'],
            userHasRole.bind(null, { atLeast: AGENT })
          )}
        >
          <IndexRedirect to="list" />
          <Route
            path="list"
            component={FollowUpsListPage}
            name="FollowUpsListPage"
          />
          <Route path="messages" component={MessagesPage} name="MessagesPage" />
        </Route>
        <Route
          path="sales-taxes"
          component={SalesTaxesPage}
          name="SalesTaxes"
          onEnter={userHasRole.bind(null, {
            exactly: [FINANCE, ADMINISTRATOR, FINTECH],
          })}
        />
        <Route
          path="transfer"
          component={ImportExportPage}
          name="Transfer"
          onEnter={userHasRole.bind(null, { atLeast: MANAGER })}
        >
          <Route
            path="import"
            component={ImportMembersPage}
            name="ImportMembers"
            onEnter={userHasRole.bind(null, { exactly: [ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER] })}
          />
          <Route path="export" component={ExportsPage} name="Exports" onEnter={userHasRole.bind(null, { exactly: [ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER] })} />
          <Route
            path="accounting-exports"
            component={AccountingExportsPage}
            name="AccountingExportsPage"
            onEnter={userHasRole.bind(null, {
              exactly: [FINANCE, ADMINISTRATOR, FINTECH],
            })}
          />
          <Route
            path="suppliers-reports"
            component={SuppliersReportsPage}
            name="SuppliersReports"
            onEnter={userHasRole.bind(null, { atLeast: MANAGER })}
          />
        </Route>
      </Route>
      <Route
        path="majorcode-recovery/:clientDesignator"
        component={MemberIdentifierRecoveryPage}
        name="MemberIdentifierRecoveryPage"
        onEnter={setLanguageCodeAsParam.bind(null, "en-CA")}
      />
      <Route
        path="recuperation-codemajor/:clientDesignator"
        component={MemberIdentifierRecoveryPage}
        name="MemberIdentifierRecoveryPage"
        onEnter={setLanguageCodeAsParam.bind(null, "fr-CA")}
      />
      <Route
        path="withdrawal/:clientDesignator/:billingPeriodKey"
        component={WithdrawalPage}
        name="WithdrawalForm"
        onEnter={setLanguageCodeAsParam.bind(null, "en-CA")}
      />
      <Route
        path="desistement/:clientDesignator/:billingPeriodKey"
        component={WithdrawalPage}
        name="WithdrawalForm"
        onEnter={setLanguageCodeAsParam.bind(null, "fr-CA")}
      />
      <Route
        path="data/:clientDesignator"
        component={PersonalDataPage}
        name="PersonalDataForm"
        onEnter={setLanguageCodeAsParam.bind(null, "en-CA")}
      />
      <Route
        path="donnees/:clientDesignator"
        component={PersonalDataPage}
        name="PersonalDataForm"
        onEnter={setLanguageCodeAsParam.bind(null, "fr-CA")}
      />
      <Route
        path="reset-password"
        component={ResetPasswordPage}
        name="ResetPasswordPage"
      />

      <Route path="example" component={ExamplePage} name="Example Page" />
      <Route
        path="unauthorized"
        component={UnauthorizedPage}
        name="Unauthorized"
      />
      <Route path="*" component={NotFoundPage} name="Not Found" />
    </Route>
  );
};



// WEBPACK FOOTER //
// ./src/components/app/routes.js